import { useEffect, useRef, useState, useCallback } from 'react'
import { Network as VisNetwork, DataSet } from 'vis-network/standalone'
import { Network, Share2, GitBranch, Settings2, AlertTriangle, Info, Pause, Play, ZoomIn, ZoomOut, Maximize2, Star, Download } from 'lucide-react'
import { networkApi, NetworkResponse, LandmarksResponse } from '../api/search'
import ResultsTable from './ResultsTable'
import { downloadCSV } from '../utils/csv'

interface NetworkVizProps {
  searchId: string
}

export default function NetworkVisualization({ searchId }: NetworkVizProps) {
  const [activeTab, setActiveTab] = useState<'citation' | 'cocitation'>('citation')
  const [networkData, setNetworkData] = useState<NetworkResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [maxNodes, setMaxNodes] = useState(150)
  const [minCitations, setMinCitations] = useState(0)
  const [minOverlap, setMinOverlap] = useState(1)
  const [nodeSizeBy, setNodeSizeBy] = useState<'citations' | 'degree' | 'fixed'>('citations')
  const [nodeColorBy, setNodeColorBy] = useState<'year' | 'quartile' | 'community'>('year')
  const [physicsEnabled, setPhysicsEnabled] = useState(true)
  const [nodeSizeScale, setNodeSizeScale] = useState(1)
  const [citationContrast, setCitationContrast] = useState(1)
  const [edgeWidthScale, setEdgeWidthScale] = useState(1)
  const [error, setError] = useState('')
  
  // Landmark state
  const [includeLandmarks, setIncludeLandmarks] = useState(false)
  const [landmarkThreshold, setLandmarkThreshold] = useState(3)
  const [landmarksData, setLandmarksData] = useState<LandmarksResponse | null>(null)
  const [landmarksLoading, setLandmarksLoading] = useState(false)
  const [showLandmarksTable, setShowLandmarksTable] = useState(false)
  
  const containerRef = useRef<HTMLDivElement>(null)
  const networkRef = useRef<VisNetwork | null>(null)
  const nodesDataSetRef = useRef<DataSet<any> | null>(null)
  const edgesDataSetRef = useRef<DataSet<any> | null>(null)
  const baseSizesRef = useRef<Map<string, number>>(new Map())

  const fetchNetwork = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = activeTab === 'citation'
        ? await networkApi.getCitationNetwork(searchId, maxNodes, minCitations, includeLandmarks, landmarkThreshold)
        : await networkApi.getCocitationNetwork(searchId, maxNodes, minOverlap, minCitations)
      setNetworkData(data)
    } catch (err: any) {
      console.error('Failed to fetch network:', err)
      setError(err.response?.data?.detail || 'Failed to fetch network data')
    } finally {
      setLoading(false)
    }
  }, [searchId, activeTab, maxNodes, minCitations, minOverlap, includeLandmarks, landmarkThreshold])

  const fetchLandmarks = useCallback(async () => {
    setLandmarksLoading(true)
    try {
      const data = await networkApi.getLandmarks(searchId, landmarkThreshold, 50)
      setLandmarksData(data)
      setShowLandmarksTable(true)
    } catch (err: any) {
      console.error('Failed to fetch landmarks:', err)
    } finally {
      setLandmarksLoading(false)
    }
  }, [searchId, landmarkThreshold])

  useEffect(() => {
    if (searchId) {
      fetchNetwork()
    }
  }, [searchId, activeTab, fetchNetwork])

  // Build vis-network data from API response
  const buildVisData = useCallback((data: NetworkResponse) => {
    if (!data.nodes) return { nodes: new DataSet([]), edges: new DataSet([]) }

    // Calculate communities (simple connected components)
    const communities = new Map<string, number>()
    const visited = new Set<string>()
    let communityId = 0
    
    const getNeighbors = (pmid: string) => {
      const neighbors = new Set<string>()
      data.edges.forEach(e => {
        if (e.source === pmid) neighbors.add(e.target)
        if (e.target === pmid) neighbors.add(e.source)
      })
      return neighbors
    }
    
    data.nodes.forEach(node => {
      if (visited.has(node.id)) return
      const queue = [node.id]
      visited.add(node.id)
      communities.set(node.id, communityId)
      
      while (queue.length > 0) {
        const current = queue.shift()!
        getNeighbors(current).forEach(neighbor => {
          if (!visited.has(neighbor)) {
            visited.add(neighbor)
            communities.set(neighbor, communityId)
            queue.push(neighbor)
          }
        })
      }
      communityId++
    })

    // Year range for color interpolation (only from non-landmark nodes)
    const regularNodes = data.nodes.filter(n => !(n as any).is_landmark)
    const years = regularNodes.map(n => n.year).filter(y => y) as number[]
    const minYear = years.length > 0 ? Math.min(...years) : 2000
    const maxYear = years.length > 0 ? Math.max(...years) : 2024

    // Color palettes
    const quartileColors: Record<string, string> = {
      Q1: '#22c55e', Q2: '#3b82f6', Q3: '#f59e0b', Q4: '#ef4444', NA: '#9ca3af'
    }
    const communityColors = [
      '#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6',
      '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b'
    ]

    // Calculate and store base sizes
    const newBaseSizes = new Map<string, number>()
    
    const visNodes = data.nodes.map(node => {
      // Check if landmark
      const isLandmark = (node as any).is_landmark === true
      
      // Base size (without scale)
      let baseSize = 12
      if (nodeSizeBy === 'citations') {
        // Apply contrast exponent: higher = more difference between low/high citations
        const logCit = Math.log1p(node.citations || 0)
        baseSize = 8 + Math.pow(logCit, citationContrast) * 3
      } else if (nodeSizeBy === 'degree') {
        const degree = data.edges.filter(e => e.source === node.id || e.target === node.id).length
        baseSize = 8 + degree * 2
      }
      newBaseSizes.set(node.id, baseSize)
      
      // Apply current scale
      const size = baseSize * nodeSizeScale

      // Color
      let color = '#3498db'
      if (isLandmark) {
        color = '#95a5a6' // Grey for landmarks
      } else if (nodeColorBy === 'quartile') {
        color = quartileColors[node.quartile || 'NA'] || '#9ca3af'
      } else if (nodeColorBy === 'community') {
        color = communityColors[(communities.get(node.id) || 0) % communityColors.length]
      } else if (nodeColorBy === 'year' && node.year) {
        const t = maxYear > minYear ? (node.year - minYear) / (maxYear - minYear) : 0
        const r = Math.round(52 + t * (231 - 52))
        const g = Math.round(152 + t * (76 - 152))
        const b = Math.round(219 + t * (60 - 219))
        color = `rgb(${r},${g},${b})`
      }

      return {
        id: node.id,
        label: '', // No labels by default (too cluttered)
        title: isLandmark 
          ? `★ LANDMARK PAPER\n${node.title}\nPMID: ${node.id}\nYear: ${node.year || 'N/A'}\nJournal: ${node.journal || 'N/A'}\nCited by dataset: ${node.citations} papers`
          : `${node.title}\nPMID: ${node.id}\nYear: ${node.year || 'N/A'}\nJournal: ${node.journal || 'N/A'}\nQuartile: ${node.quartile || 'N/A'}\nCitations: ${node.citations}\nRCR: ${node.rcr?.toFixed(2) || 'N/A'}`,
        size: size,
        color: {
          background: color,
          border: color,
          highlight: { background: color, border: '#1e40af' },
          hover: { background: color, border: '#1e40af' }
        },
        borderWidth: isLandmark ? 3 : 2,
        borderWidthSelected: isLandmark ? 5 : 4,
        font: { size: 10, color: '#374151' },
        shape: isLandmark ? 'diamond' : 'dot',
        year: node.year,
        journal: node.journal,
        quartile: node.quartile,
        citations: node.citations,
        rcr: node.rcr,
        is_landmark: isLandmark,
      }
    })

    const visEdges = data.edges.map((edge, i) => {
      let width = 1.5
      if (activeTab === 'cocitation' && edge.weight) {
        width = Math.max(1, edge.weight * 5 * edgeWidthScale)
      } else {
        width = 1.5 * edgeWidthScale
      }
      
      return {
        id: `e${i}`,
        from: edge.source,
        to: edge.target,
        width: width,
        color: { color: '#cbd5e1', highlight: '#3b82f6', hover: '#3b82f6' },
        arrows: activeTab === 'citation' ? { to: { enabled: true, scaleFactor: 0.5 } } : undefined,
        title: activeTab === 'cocitation' && edge.weight 
          ? `Jaccard: ${edge.weight}\nOverlap: ${edge.overlap || 'N/A'}` 
          : undefined,
        smooth: { enabled: true, type: 'continuous', roundness: 0.2 } as any,
      }
    })

    // Store base sizes for slider scaling
    baseSizesRef.current = newBaseSizes

    return {
      nodes: new DataSet(visNodes),
      edges: new DataSet(visEdges)
    }
  }, [activeTab, nodeSizeBy, nodeColorBy, nodeSizeScale, citationContrast, edgeWidthScale])

  // Initialize or update network
  useEffect(() => {
    if (!containerRef.current || !networkData || !networkData.nodes || networkData.nodes.length === 0) return

    const { nodes, edges } = buildVisData(networkData)
    nodesDataSetRef.current = nodes
    edgesDataSetRef.current = edges

    if (networkRef.current) {
      networkRef.current.destroy()
    }

    const options = {
      nodes: {
        shape: 'dot',
        shadow: { enabled: true, size: 5, x: 2, y: 2 }
      },
      edges: {
        smooth: { enabled: true, type: 'continuous', roundness: 0.5 } as any,
        selectionWidth: 3,
        hoverWidth: 2,
        shadow: { enabled: false }
      },
      physics: {
        enabled: true, // Always start with physics enabled
        barnesHut: {
          gravitationalConstant: -3000,
          centralGravity: 0.3,
          springLength: 120,
          springConstant: 0.04,
          damping: 0.09,
          avoidOverlap: 0.2
        },
        stabilization: {
          enabled: true,
          iterations: 100,
          updateInterval: 25,
          onlyDynamicEdges: false,
          fit: true
        }
      },
      interaction: {
        dragNodes: true,
        dragView: true,
        zoomView: true,
        hover: true,
        tooltipDelay: 200,
        hideEdgesOnDrag: false,
        hideNodesOnDrag: false
      },
      layout: {
        randomSeed: 42
      }
    }

    networkRef.current = new VisNetwork(containerRef.current, { nodes, edges }, options)

    // Fit view after stabilization
    networkRef.current.once('stabilizationIterationsDone', () => {
      networkRef.current?.fit({ animation: { duration: 500, easingFunction: 'easeInOutQuad' } as any })
    })

    // Apply initial physics state
    if (!physicsEnabled) {
      networkRef.current.setOptions({ physics: { enabled: false } })
    }

    return () => {
      if (networkRef.current) {
        networkRef.current.destroy()
        networkRef.current = null
      }
    }
  }, [networkData, buildVisData])

  // Update node sizes when scale changes (without rebuilding)
  useEffect(() => {
    if (!networkRef.current || !nodesDataSetRef.current) return
    
    const updates: { id: string; size: number }[] = []
    nodesDataSetRef.current.forEach((node: any) => {
      const baseSize = baseSizesRef.current.get(node.id)
      if (baseSize !== undefined) {
        updates.push({ id: node.id, size: baseSize * nodeSizeScale })
      }
    })
    
    if (updates.length > 0) {
      nodesDataSetRef.current.update(updates)
      networkRef.current.redraw()
    }
  }, [nodeSizeScale])

  // Update physics
  useEffect(() => {
    if (!networkRef.current) return
    networkRef.current.setOptions({
      physics: { enabled: physicsEnabled }
    })
  }, [physicsEnabled])

  const handleZoomIn = () => {
    if (!networkRef.current) return
    const scale = networkRef.current.getScale()
    networkRef.current.moveTo({ scale: scale * 1.3, animation: { duration: 300, easingFunction: 'easeInOutQuad' } as any })
  }

  const handleZoomOut = () => {
    if (!networkRef.current) return
    const scale = networkRef.current.getScale()
    networkRef.current.moveTo({ scale: scale / 1.3, animation: { duration: 300, easingFunction: 'easeInOutQuad' } as any })
  }

  const handleFit = () => {
    networkRef.current?.fit({ animation: { duration: 500, easingFunction: 'easeInOutQuad' } as any })
  }

  // No citation data state
  if (!loading && networkData && !networkData.has_citation_data) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setActiveTab('citation')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'citation'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50 border'
            }`}
          >
            <GitBranch className="w-4 h-4" />
            Citation Network
          </button>
          <button
            onClick={() => setActiveTab('cocitation')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'cocitation'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50 border'
            }`}
          >
            <Share2 className="w-4 h-4" />
            Co-citation Network
          </button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800 mb-2">No Citation Data Available</h3>
              <p className="text-amber-700 mb-3">{networkData.message}</p>
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <h4 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Steps to build citation networks:
                </h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600">
                  <li>Perform a search</li>
                  <li>Click <strong>"Fetch iCite Metrics"</strong> button in the sidebar</li>
                  <li>Wait for the fetch to complete (10-30 seconds)</li>
                  <li>Return to this tab and click <strong>"Refresh"</strong></li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Stats
  const stats = networkData ? {
    nodes: networkData.count,
    edges: networkData.edge_count,
    density: networkData.count > 1 ? (networkData.edge_count / (networkData.count * (networkData.count - 1) / 2)).toFixed(3) : '0'
  } : null

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setActiveTab('citation')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'citation'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50 border'
            }`}
          >
            <GitBranch className="w-4 h-4" />
            Citation Graph
          </button>
          <button
            onClick={() => setActiveTab('cocitation')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'cocitation'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50 border'
            }`}
          >
            <Share2 className="w-4 h-4" />
            Co-citation Network
          </button>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          <Settings2 className="w-4 h-4 text-slate-400" />
          
          <div className="flex items-center gap-1">
            <label className="text-xs text-slate-500">Max nodes:</label>
            <input
              type="number"
              value={maxNodes}
              onChange={(e) => setMaxNodes(parseInt(e.target.value) || 50)}
              className="w-14 px-2 py-1 text-xs border rounded"
            />
          </div>
          
          <div className="flex items-center gap-1">
            <label className="text-xs text-slate-500">Min cit:</label>
            <input
              type="number"
              value={minCitations}
              onChange={(e) => setMinCitations(parseInt(e.target.value) || 0)}
              className="w-12 px-2 py-1 text-xs border rounded"
            />
          </div>
          
          {activeTab === 'cocitation' && (
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Min overlap:</label>
              <input
                type="number"
                value={minOverlap}
                onChange={(e) => setMinOverlap(parseInt(e.target.value) || 1)}
                className="w-10 px-2 py-1 text-xs border rounded"
              />
            </div>
          )}
          
          <select
            value={nodeSizeBy}
            onChange={(e) => setNodeSizeBy(e.target.value as any)}
            className="text-xs border rounded px-2 py-1"
          >
            <option value="citations">Size: Citations</option>
            <option value="degree">Size: Degree</option>
            <option value="fixed">Size: Fixed</option>
          </select>
          
          <select
            value={nodeColorBy}
            onChange={(e) => setNodeColorBy(e.target.value as any)}
            className="text-xs border rounded px-2 py-1"
          >
            <option value="year">Color: Year</option>
            <option value="quartile">Color: Quartile</option>
            <option value="community">Color: Community</option>
          </select>
          
          <button
            onClick={() => setPhysicsEnabled(!physicsEnabled)}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded border ${
              physicsEnabled ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-600'
            }`}
          >
            {physicsEnabled ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            {physicsEnabled ? 'Freeze' : 'Animate'}
          </button>
          
          <button
            onClick={fetchNetwork}
            disabled={loading}
            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Landmark Controls - only for citation network */}
      {activeTab === 'citation' && (
        <div className="bg-white rounded-lg border p-3 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeLandmarks}
              onChange={(e) => setIncludeLandmarks(e.target.checked)}
              className="w-4 h-4"
              id="include-landmarks"
            />
            <label htmlFor="include-landmarks" className="text-xs font-medium text-slate-700 cursor-pointer">
              Include landmark papers in network
            </label>
          </div>
          
          {includeLandmarks && (
            <>
              <div className="flex items-center gap-1">
                <label className="text-xs text-slate-500">Min dataset citations:</label>
                <input
                  type="number"
                  value={landmarkThreshold}
                  onChange={(e) => setLandmarkThreshold(parseInt(e.target.value) || 1)}
                  min={1}
                  max={50}
                  className="w-12 px-2 py-1 text-xs border rounded"
                />
              </div>
              
              <button
                onClick={fetchLandmarks}
                disabled={landmarksLoading}
                className="flex items-center gap-1 px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
              >
                <Star className="w-3 h-3" />
                {landmarksLoading ? 'Finding...' : 'Find Landmarks'}
              </button>
            </>
          )}
          
          {!includeLandmarks && (
            <span className="text-xs text-slate-400">Landmark papers = cited by many dataset papers but not in the dataset</span>
          )}
        </div>
      )}

      {/* Size sliders */}
      {networkData && networkData.count > 0 && (
        <div className="space-y-2">
          <div className="bg-white rounded-lg border p-3 flex items-center gap-4">
            <span className="text-xs font-medium text-slate-600">Node Size Scale:</span>
            <input
              type="range"
              min="0.3"
              max="3"
              step="0.1"
              value={nodeSizeScale}
              onChange={(e) => setNodeSizeScale(parseFloat(e.target.value))}
              className="flex-1 max-w-xs"
            />
            <span className="text-xs text-slate-500 w-12">{nodeSizeScale.toFixed(1)}x</span>
            <span className="text-xs text-slate-400">Drag to resize all nodes</span>
          </div>
          
          {nodeSizeBy === 'citations' && (
            <div className="bg-white rounded-lg border p-3 flex items-center gap-4">
              <span className="text-xs font-medium text-slate-600">Citation Contrast:</span>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={citationContrast}
                onChange={(e) => setCitationContrast(parseFloat(e.target.value))}
                className="flex-1 max-w-xs"
              />
              <span className="text-xs text-slate-500 w-12">{citationContrast.toFixed(1)}</span>
              <span className="text-xs text-slate-400">Higher = highly-cited papers much bigger</span>
            </div>
          )}
          
          <div className="bg-white rounded-lg border p-3 flex items-center gap-4">
            <span className="text-xs font-medium text-slate-600">Edge Width Scale:</span>
            <input
              type="range"
              min="0.3"
              max="3"
              step="0.1"
              value={edgeWidthScale}
              onChange={(e) => setEdgeWidthScale(parseFloat(e.target.value))}
              className="flex-1 max-w-xs"
            />
            <span className="text-xs text-slate-500 w-12">{edgeWidthScale.toFixed(1)}x</span>
            <span className="text-xs text-slate-400">Drag to thicken/thin all edges</span>
          </div>
        </div>
      )}

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-sm text-blue-800 mb-1">Citation Graph</h4>
          <p className="text-xs text-blue-700">Shows which papers from your dataset cite each other.</p>
          <ul className="text-xs text-blue-600 mt-1 space-y-0.5 list-disc list-inside">
            <li>Nodes = papers from your search</li>
            <li>Edges = "Paper A cites Paper B" (directional)</li>
            <li>Tip: Color by Year (blue→red), size by Citations</li>
          </ul>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-sm text-green-800 mb-1">Co-citation Network</h4>
          <p className="text-xs text-green-700">Shows papers cited by the same other papers.</p>
          <ul className="text-xs text-green-600 mt-1 space-y-0.5 list-disc list-inside">
            <li>Nodes = papers from your search</li>
            <li>Edges = "Paper A & B cited together" (undirected)</li>
            <li>Tip: Thicker edges = cited together more often</li>
          </ul>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center h-96 text-slate-500">
          <Network className="w-8 h-8 animate-pulse" />
          <span className="ml-2">Loading network...</span>
        </div>
      )}

      {!loading && networkData && networkData.count > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-slate-800">
                {activeTab === 'citation' ? 'Citation Network' : 'Co-citation Network'}
              </h3>
              <p className="text-xs text-slate-500">{networkData.message}</p>
            </div>
            
            <div className="flex items-center gap-2">
              {nodeColorBy === 'quartile' && (
                <div className="flex items-center gap-2 text-xs">
                  {['Q1', 'Q2', 'Q3', 'Q4', 'NA'].map(q => (
                    <div key={q} className="flex items-center gap-1">
                      <div className={`w-3 h-3 rounded-full ${
                        q === 'Q1' ? 'bg-green-500' :
                        q === 'Q2' ? 'bg-blue-500' :
                        q === 'Q3' ? 'bg-amber-500' :
                        q === 'Q4' ? 'bg-red-500' : 'bg-gray-400'
                      }`} />
                      <span className="text-slate-600">{q}</span>
                    </div>
                  ))}
                </div>
              )}
              {nodeColorBy === 'year' && (
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span className="text-slate-600">Old</span>
                  <span>→</span>
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-slate-600">New</span>
                </div>
              )}
               
              <div className="flex items-center gap-1">
                <button onClick={handleZoomIn} className="p-1.5 hover:bg-slate-100 rounded" title="Zoom in">
                  <ZoomIn className="w-4 h-4 text-slate-600" />
                </button>
                <button onClick={handleZoomOut} className="p-1.5 hover:bg-slate-100 rounded" title="Zoom out">
                  <ZoomOut className="w-4 h-4 text-slate-600" />
                </button>
                <button onClick={handleFit} className="p-1.5 hover:bg-slate-100 rounded" title="Fit to screen">
                  <Maximize2 className="w-4 h-4 text-slate-600" />
                </button>
              </div>
            </div>
          </div>
          
          <div 
            ref={containerRef}
            style={{ height: '650px', width: '100%' }}
          />
          
          {/* Stats footer */}
          {stats && (
            <div className="border-t p-3 bg-slate-50">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-lg font-bold text-slate-800">{stats.nodes}</div>
                  <div className="text-xs text-slate-500">Nodes</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-slate-800">{stats.edges}</div>
                  <div className="text-xs text-slate-500">Edges</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-slate-800">{stats.density}</div>
                  <div className="text-xs text-slate-500">Density</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-slate-800">
                    {activeTab === 'citation' ? 'Directed' : 'Undirected'}
                  </div>
                  <div className="text-xs text-slate-500">Type</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && networkData && networkData.count === 0 && (
        <div className="text-center py-12 text-slate-500">
          <Network className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>No network could be built.</p>
          <p className="text-xs mt-2">Try lowering minimum citations or overlap thresholds.</p>
        </div>
      )}

      {/* Landmark Papers Table */}
      {showLandmarksTable && landmarksData && landmarksData.count > 0 && (
        <LandmarkTable 
          landmarks={landmarksData.landmarks} 
          onClose={() => setShowLandmarksTable(false)} 
        />
      )}

      {showLandmarksTable && landmarksData && landmarksData.count === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="text-sm text-amber-700">{landmarksData.message}</span>
        </div>
      )}
    </div>
  )
}

// Landmark Table Component
function LandmarkTable({ landmarks, onClose }: { landmarks: any[]; onClose: () => void }) {
  // Map landmarks to article format for ResultsTable
  const articles = landmarks.map(l => ({
    pmid: l.pmid,
    title: l.title,
    authors: Array.isArray(l.authors) ? l.authors : (l.authors ? l.authors.split(', ') : []),
    authors_full: l.authors_full || (Array.isArray(l.authors) ? l.authors.join(', ') : l.authors),
    journal: l.journal,
    year: l.year,
    abstract: l.abstract,
    doi: l.doi,
    keywords: l.keywords,
    mesh_terms: l.mesh_terms,
    dataset_citations: l.count, // Times cited by papers in the dataset
  }))

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Landmark Papers
          </h3>
          <p className="text-xs text-slate-500">
            Papers frequently cited by your dataset but not in it. Influential 'hub' papers.
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          Hide
        </button>
      </div>

      <div className="p-4">
        <div className="flex justify-end mb-4">
          <button
            onClick={() => downloadCSV(articles, `landmark_papers_${new Date().toISOString().split('T')[0]}.csv`)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-sm"
          >
            <Download className="w-4 h-4" />
            Download CSV
          </button>
        </div>
        <ResultsTable articles={articles} />
      </div>
    </div>
  )
}
